import { makeApi, Zodios, type ZodiosInstance, type ZodiosOptions } from '@zodios/core'
import { z } from 'zod'

const TaskWeaponsOfChoiceEnum = z.enum(['SQL', 'Python'])
const TaskDifficultyEnum = z.enum(['Easy', 'Intermediate', 'Hard', 'Expert'])
const Task = z
  .object({
    name: z.string().min(3).max(200),
    body: z.string().min(10).max(50000),
    teaser: z.string().min(10).max(200),
    ownerId: z
      .string()
      .min(1)
      .max(50)
      .regex(/^[a-zA-Z0-9_-]+$/)
      .optional(),
    allowedWeapons: z.array(TaskWeaponsOfChoiceEnum).min(1).max(2).optional(),
    difficulty: TaskDifficultyEnum,
    description: z.string().min(20).max(10000),
    defaultQuery: z.string().min(1).max(2000),
    seedFactor: z.number(),
    feedbackPayload: z.string().min(2).optional(),
    dataBank: z.string().min(2),
    accessGroups: z
      .array(
        z
          .string()
          .min(1)
          .max(50)
          .regex(/^[a-zA-Z0-9_-]+$/)
      )
      .min(1)
      .max(10),
    proficiencies: z
      .array(
        z.enum([
          'joins',
          'dataSanitization',
          'anomalyDetection',
          'timeSeriesAnalysis',
          'windowFunctions',
          'aggregation',
        ])
      )
      .optional(),
    solutionPlaceholder: z.string().optional(),
    availabilityWeight: z.number().optional(),
    deleted: z.boolean().optional(),
    sharedDBName: z.string().optional(),
    customQueryTimeout: z.number().gte(1).lte(255).optional(),
    slug: z.string().optional(),
    coverImageUrl: z.string().optional(),
    coverFocusY: z.number().gte(0).lte(100).optional(),
  })
  .passthrough()
  .and(z.object({ id: z.string() }).passthrough())
const FeedbackPersonalInfo = z
  .object({ yearsOfXP: z.number().gte(0).lte(10) })
  .partial()
  .passthrough()
const FeedbackUser = z
  .object({ id: z.string(), personalInfo: FeedbackPersonalInfo.optional() })
  .passthrough()
const SubmitSolutionRequest = z
  .object({
    challengeId: z.string(),
    task: Task,
    solution: z.string(),
    weaponOfChoice: TaskWeaponsOfChoiceEnum,
    jNotebook: z.object({}).partial().passthrough().optional(),
    user: FeedbackUser,
    insightsAudience: z
      .enum(['school', 'employer'])
      .optional()
      .default('employer'),
    is_employer_request: z.boolean().optional(),
    feedback_language: z
      .enum(['en', 'he', 'es', 'fr', 'de'])
      .optional()
      .default('en'),
  })
  .passthrough()
const FeedbackSection = z
  .object({
    key: z.string(),
    title: z.string(),
    body_md: z.string().min(1).optional(),
    items: z.array(z.string()).min(1).optional(),
  })
  .passthrough()
const FeedbackAudience = z
  .object({
    grade: z.number().int().gte(1).lte(10),
    sections: z.array(FeedbackSection),
  })
  .passthrough()
const Feedback = z
  .object({
    tips: z.array(z.string()).max(3),
    excerpt: z.string(),
    userFeedback: FeedbackAudience,
    partnerFeedback: FeedbackAudience.nullable(),
  })
  .passthrough()
const ErrorResponse = z
  .object({
    error: z.string(),
    code: z.string(),
    details: z.object({}).partial().passthrough(),
  })
  .partial()
  .passthrough()
const FeedbackExcerptResponse = z
  .object({ excerpt: z.string().max(280) })
  .passthrough()
const DetachFeedbackResponse = z
  .object({
    success: z.boolean(),
    detached_feedbacks: z.number().int().optional(),
    detached_statuses: z.number().int().optional(),
  })
  .passthrough()
const FeedbackStatusEnum = z.enum([
  'unprocessed',
  'processing1',
  'processing2',
  'processing3',
  'processing4',
  'processing5',
  'processing6',
  'processing7',
  'processing8',
  'processing9',
  'processing10',
  'processing11',
  'processing12',
  'processing13',
  'processing14',
  'processing15',
  'failed',
  'processed',
])
const FeedbackResultResponse = z
  .object({ status: FeedbackStatusEnum, feedback: Feedback.optional() })
  .passthrough()

export const schemas = {
  TaskWeaponsOfChoiceEnum,
  TaskDifficultyEnum,
  Task,
  FeedbackPersonalInfo,
  FeedbackUser,
  SubmitSolutionRequest,
  FeedbackSection,
  FeedbackAudience,
  Feedback,
  ErrorResponse,
  FeedbackExcerptResponse,
  DetachFeedbackResponse,
  FeedbackStatusEnum,
  FeedbackResultResponse,
}

const endpoints: ReturnType<typeof makeApi> = makeApi([
  {
    method: 'post',
    path: '/detach-feedback/:challenge_id',
    alias: 'detachFeedback',
    description: `Marks existing feedback and feedback_status rows for a challenge as superseded, allowing a new submission for the same challenge_id.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'challenge_id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: DetachFeedbackResponse,
    errors: [
      {
        status: 404,
        description: `No feedback found for challenge`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/feedback',
    alias: 'submitSolution',
    description: `Submit a solution for a challenge task`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SubmitSolutionRequest,
      },
    ],
    response: Feedback,
    errors: [
      {
        status: 400,
        description: `Bad request - invalid input data`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/feedback-excerpt/:challenge_id',
    alias: 'generateFeedbackExcerpt',
    description: `Generate one short positive learner-facing excerpt from the latest active senior review for a challenge.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'challenge_id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.object({ excerpt: z.string().max(280) }).passthrough(),
    errors: [
      {
        status: 404,
        description: `No active feedback found for challenge`,
        schema: ErrorResponse,
      },
      {
        status: 409,
        description: `Senior review data is unavailable or invalid`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/feedback-result/:challenge_id',
    alias: 'getFeedbackResult',
    description: `Get the feedback result for a submitted solution`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'challenge_id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: FeedbackResultResponse,
    errors: [
      {
        status: 404,
        description: `Route not found`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
])

export const api: ZodiosInstance<typeof endpoints> = new Zodios(endpoints)

export function createApiClient(baseUrl: string, options?: ZodiosOptions): ZodiosInstance<typeof endpoints> {
  return new Zodios(baseUrl, endpoints, options)
}
